'use client'

import { useEffect, useRef } from 'react'
import { useStytchMember } from '@stytch/nextjs/b2b'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import {
  clearAppliedClientPortalSessionPartnerKey,
  CLIENT_PORTAL_LOGIN_SESSION_MINUTES,
  decideClientPortalSessionRefresh,
  readAppliedClientPortalSessionMinutes,
  readAppliedClientPortalSessionPartnerKey,
  recordAppliedClientPortalSessionSuccess,
  rememberAppliedClientPortalSessionPartner,
  type ClientPortalSessionPartner,
} from '@/utilities/stytch/clientPortalSessionLength'
import { getStytchJWT } from '@/utilities/stytch/getStytchJWT'
import { isImpersonatingSession } from '@/utilities/stytch/isImpersonatingSession'
import { stytchClient } from '@/utilities/stytch/stytchClient'

type ClientPortalSessionLengthEffectProps = {
  partners: readonly ClientPortalSessionPartner[]
  partnersSettled: boolean
  partnersLoadFailed: boolean
  partnerId: string | null
}

// The effect inputs can stay the same after a failed refresh.
// These attempts run in this effect so recovery does not wait for a new render.
export const CLIENT_PORTAL_SESSION_REFRESH_ATTEMPT_LIMIT = 3
export const CLIENT_PORTAL_SESSION_REFRESH_RETRY_DELAY_MS = 1000

type SessionRefreshRetryWait = {
  timer: ReturnType<typeof setTimeout> | null
  stop: (() => void) | null
}

function stopSessionRefreshRetry(wait: SessionRefreshRetryWait) {
  const stop = wait.stop
  wait.stop = null
  stop?.()
}

function waitForSessionRefreshRetry(wait: SessionRefreshRetryWait, delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      if (wait.timer !== null) {
        clearTimeout(wait.timer)
        wait.timer = null
      }
      if (wait.stop === finish) wait.stop = null
      resolve()
    }

    wait.stop = finish
    wait.timer = setTimeout(finish, delayMs)
  })
}

export function ClientPortalSessionLengthEffect({
  partners,
  partnersSettled,
  partnersLoadFailed,
  partnerId,
}: Readonly<ClientPortalSessionLengthEffectProps>) {
  const { member } = useStytchMember()
  const requestIdRef = useRef(0)
  const queueRef = useRef(Promise.resolve())
  const retryWaitRef = useRef<SessionRefreshRetryWait>({ timer: null, stop: null })
  const memberId = member?.member_id?.trim() || null

  useEffect(() => {
    if (!memberId) return
    // An impersonated session keeps STYTCH_ACTIVE_SESSION_DURATION.
    // establishImpersonatedClientSession sets that length.
    // This effect skips the partner flag client-portal-session-length for that session.
    // A regular client login uses the partner flag.
    // A 10-minute partner setting applies to that login.
    if (isImpersonatingSession(getStytchJWT())) return

    const decision = decideClientPortalSessionRefresh({
      memberId,
      partnersSettled,
      partnersLoadFailed,
      partnerId,
      partners,
      lastAppliedKey: readAppliedClientPortalSessionPartnerKey(memberId),
      lastSuccessfulMinutes: readAppliedClientPortalSessionMinutes(memberId),
    })

    if (decision.type === 'wait' || decision.type === 'skip') return

    if (decision.type === 'remember') {
      rememberAppliedClientPortalSessionPartner(
        memberId,
        decision.partnerKey,
        CLIENT_PORTAL_LOGIN_SESSION_MINUTES,
      )
      return
    }

    rememberAppliedClientPortalSessionPartner(memberId, decision.partnerKey)

    const requestId = ++requestIdRef.current
    stopSessionRefreshRetry(retryWaitRef.current)
    const minutes = decision.minutes
    const retryWait = retryWaitRef.current
    queueRef.current = queueRef.current
      .catch(() => undefined)
      .then(async () => {
        for (
          let attempt = 1;
          attempt <= CLIENT_PORTAL_SESSION_REFRESH_ATTEMPT_LIMIT;
          attempt += 1
        ) {
          if (requestIdRef.current !== requestId) return
          try {
            await stytchClient.session.authenticate({
              session_duration_minutes: minutes,
            })
            if (requestIdRef.current !== requestId) return
            recordAppliedClientPortalSessionSuccess(memberId, decision.partnerKey, minutes)
            trackEvent('client:session_length_refresh_success', {
              sessionDurationMinutes: minutes,
              partnerKey: decision.partnerKey,
            })
            return
          } catch (error) {
            if (requestIdRef.current !== requestId) return
            const attemptsRemain = attempt < CLIENT_PORTAL_SESSION_REFRESH_ATTEMPT_LIMIT
            // The partner key stays until the last attempt fails.
            // The last failure clears that key and keeps the last successful duration.
            if (
              !attemptsRemain &&
              readAppliedClientPortalSessionPartnerKey(memberId) === decision.partnerKey
            ) {
              clearAppliedClientPortalSessionPartnerKey(memberId)
            }
            trackEvent('client:session_length_refresh_fail', {
              ...errorAnalyticsPayload(error),
              sessionDurationMinutes: minutes,
              partnerKey: decision.partnerKey,
            })
            if (!attemptsRemain) return
            await waitForSessionRefreshRetry(
              retryWait,
              CLIENT_PORTAL_SESSION_REFRESH_RETRY_DELAY_MS,
            )
          }
        }
      })
  }, [memberId, partnerId, partners, partnersLoadFailed, partnersSettled])

  // Stop a queued refresh when this component unmounts.
  // A new mount has a new request counter. That counter does not stop the old queue.
  useEffect(() => {
    const retryWait = retryWaitRef.current
    return () => {
      requestIdRef.current += 1
      stopSessionRefreshRetry(retryWait)
    }
  }, [])

  return null
}
